/**
 * @file    CprNavigationContext.tsx
 * @scope   CPR
 * @summary Owns the CPR Manage Programs context (rollup under edition + Cancel snapshot).
 * @consumes common/types CprProgramsContext
 * @produces CprNavigationProvider, useCprNavigation
 * @sideEffects None. Local state only.
 * @notes Moved out of `useDrawerNavigation` so the drawer stays unaware of CPR sub-views.
 */
import React, { createContext, useContext, useMemo, useState } from 'react';
import { CprProgramsContext } from '../../../common/types';

export type CprNavigationValue = {
  cprProgramsContext: CprProgramsContext;
  setCprProgramsContext: React.Dispatch<React.SetStateAction<CprProgramsContext>>;
};

const CprNavigationContext = createContext<CprNavigationValue | null>(null);

export const CprNavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cprProgramsContext, setCprProgramsContext] = useState<CprProgramsContext>(null);
  const value = useMemo(
    () => ({ cprProgramsContext, setCprProgramsContext }),
    [cprProgramsContext]
  );

  return <CprNavigationContext.Provider value={value}>{children}</CprNavigationContext.Provider>;
};

export const useCprNavigation = (): CprNavigationValue => {
  const ctx = useContext(CprNavigationContext);
  if (!ctx) throw new Error('useCprNavigation must be used within its provider');
  return ctx;
};
